<template>
  <component
    :clientType="clientType"
    :email="$route.query.email"
    :is="componentName"
    :requestKey="$route.query.key"
    :error="errorMessage"
    :success="successMessage"
    :form="form"
    v-bind="$route.query"
  />
</template>

<script>
import { LegacyQueryRoutes } from 'client/routes/RouteGenerator';
import AdminPasswordResetAndSetup from 'client/containers/AdminPasswordResetAndSetup.vue';
import AdminTOTPReset from 'client/containers/AdminTOTPReset.vue';
import AdminTotpSetup from 'client/containers/AdminTotpSetup/AdminTotpSetup.vue';
import ChooseOS from 'client/containers/ChooseOS.vue';
import Constants, { LoginConsts } from 'client/util/Constants';
import Cookies from 'js-cookie';
import MacPasswordReset from 'client/containers/MacPasswordReset.vue';
import OauthLoginWrapper from 'client/containers/Login/OauthLoginWrapper.vue';
import RequestPasswordReset from 'client/containers/RequestPasswordReset.vue';
import RequestTOTPReset from 'client/containers/RequestTOTPReset.vue';
import SsoLoginWrapper from 'client/containers/Login/SsoLoginWrapper.vue';
import UserLoginWrapper from 'client/containers/Login/UserLoginWrapper.vue';
import UserRegistration from 'client/containers/UserRegistration.vue';
import UserResetPassword from 'client/containers/UserResetPassword.vue';
import VerifyTOTP from 'client/containers/VerifyTOTP.vue';
import WindowsPasswordReset from 'client/containers/WindowsPasswordReset.vue';
import api from 'client/api';

const { clientTypeCookie, pushStatusMessages } = LoginConsts;
const { admin, user } = LoginConsts.clientType;
const { timezoneOffsetMinutes } = Constants;

export default {
  name: 'LoginApp',
  data: () => ({
    clientType: admin,
    componentName: '',
  }),
  props: {
    form: {
      type: Object,
      default: () => ({
        email: '',
        password: '',
      }),
    },
  },
  components: {
    AdminPasswordResetAndSetup,
    AdminTOTPReset,
    AdminTotpSetup,
    ChooseOS,
    UserLoginWrapper,
    VerifyTOTP,
    MacPasswordReset,
    OauthLoginWrapper,
    RequestPasswordReset,
    RequestTOTPReset,
    SsoLoginWrapper,
    UserRegistration,
    UserResetPassword,
    WindowsPasswordReset,
  },
  /**
   * Used to render the current login view on initial render of the app
   */
  created() {
    this.renderLoginRoute();
  },
  computed: {
    /**
     * Used to map url error codes (?error=myCode) to a friendly message.
     */
    errorMessage() {
      return pushStatusMessages[this.$route.query.error] || '';
    },

    successMessage() {
      return pushStatusMessages[this.$route.query.success] || '';
    },
  },
  methods: {
    /**
     * Initializes the api specified by client type.
     * @param  {String} componentName [Name of the page rendered by vue]
     */
    async initializeApi(componentName) {
      if (componentName === 'UserLoginWrapper' || componentName === 'SsoLoginWrapper') {
        await api.user.validateDeviceCert().catch(() => {});
      }
    },
    /**
     * If there is a previous client type then return it, otherwise user is the default.
     * @return {String} [can have values 'admin' || 'user']
     */
    getPreviousClientType() {
      return Cookies.get(clientTypeCookie) || user;
    },

    /**
     * Gets the users current timezone offset and sets it in the cookie.
     * @param {String} the window location protocol
     */
    setTimezoneCookie(protocol) {
      const cookieOption = (protocol === 'https:') ? { secure: true } : null;
      Cookies.set(timezoneOffsetMinutes, new Date().getTimezoneOffset(), cookieOption);
    },

    renderLoginRoute() {
      // Three possible legacy query params that determine which Vue component to render
      const { template, context } = this.$route.query;
      const { hash } = this.$route;
      // Whichever query param is present will be used to get the route
      const LegacyRouteKey = template || context || hash;
      // get the data specific to the route, else get the default
      const routeData = LegacyQueryRoutes[LegacyRouteKey] || LegacyQueryRoutes.default;
      let { clientType } = routeData;
      // csrfType does not seem to be used anywhere
      const { componentName } = routeData;

      // set the client type cookie
      if (!clientType) {
        clientType = this.getPreviousClientType();
      }

      // Set the previous client type cookie to the current client type
      Cookies.set(clientTypeCookie, clientType);

      this.setTimezoneCookie(window.location.protocol);
      this.initializeApi(componentName).then(() => {
        this.clientType = clientType;
        this.componentName = componentName;
      });
    },
  },
  watch: {
    /**
     * The route object needs to be watched for when the url changes without a full window refresh.
     * For instance when the user navigates to #/new-org-verification the LoginApp will not rerender
     * without this.
     */
    $route() {
      this.renderLoginRoute();
    },
  },
};
</script>
